import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  output,
  Renderer2,
  ChangeDetectionStrategy,
  viewChild
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { L10nTranslationService } from 'angular-l10n';
import { DataObject } from '@miq/shared-models';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-table-paginator',
  templateUrl: './table-paginator.component.html',
  styleUrl: './table-paginator.component.scss',
  imports: [MatPaginator],
  providers: [
    {
      provide: MatPaginatorIntl,
      useFactory: (l10n: L10nTranslationService): MatPaginatorIntl => {
        const intl = new MatPaginatorIntl();
        intl.itemsPerPageLabel = l10n.translate('recordsSearch.itemsPerPage');
        return intl;
      },
      deps: [L10nTranslationService],
    },
  ],
})
export class TablePaginatorComponent implements AfterViewInit, OnDestroy {
  readonly paginator = viewChild('paginator', { read: ElementRef });
  // * For datasource table usage...
  readonly dataPaginator = viewChild(MatPaginator);
  readonly pageIndex = model(0);
  length = input<number>();
  isGroupedView = input<boolean>();
  groupViewAcc = input<number>();
  readonly pageSizeOptionsInput = input<number[]>([50, 200], { alias: 'pageSizeOptions' });
  pageSizeOptions = [50, 200];
  readonly pageSizeInput = input<number>(this.pageSizeOptions[0], { alias: 'pageSize' });
  pageSize = this.pageSizeOptions[0];
  useDataSource = input(false);
  pageChange = output<number>();
  constructor() {
    effect(() => {
      const v = this.pageSizeOptionsInput();
      if (v !== undefined) this.pageSizeOptions = v;
    });
    effect(() => {
      const v = this.pageSizeInput();
      if (v !== undefined) this.pageSize = v;
    });
    effect(() => {
      this.groupViewAcc();
      if (!this.groupViewAccInitialized) {
        this.groupViewAccInitialized = true;
        return;
      }
      this.updateButtonStylesByGroupFlag(false);
    });
    effect(() => {
      const grouped = this.isGroupedView();
      const firstChange = !this.isGroupedViewInitialized;
      const prev = this.prevIsGroupedView;
      if (!firstChange) {
        // resetting the page index if groupby is toggled
        this.pageIndex.set(0);
        if (grouped) {
          this.cleanDOM(this.numOfPages);
          this.setUpDOM();
          this.updatePages();
        } else if (prev) {
          this.setUpPages();
          this.updatePages();
        }
      }
      this.prevIsGroupedView = grouped;
      this.isGroupedViewInitialized = true;
    });
    effect(() => {
      const idx = this.pageIndex();
      if (!this.pageIndexInitialized) {
        // first change behavior
        this.pageGroup = Math.max(idx % 2 === 0 ? idx - 3 : idx - 2, 1);
        this.pageIndexInitialized = true;
        this.prevPageIndex = idx;
        return;
      }
      if (!this.length() && this.prevPageIndex !== idx) {
        this.updatePages();
      }
      this.prevPageIndex = idx;
    });
    effect(() => {
      const len = this.length();
      if (!this.lengthInitialized) {
        this.lengthInitialized = true;
        this.prevLength = len;
        return;
      }
      if (this.prevLength !== len) {
        const currentNumOfPages = Math.ceil(len / this.pageSize);
        if (this.prevLength) {
          this.cleanDOM(currentNumOfPages);
        }
        if (!this.validateDOM()) {
          this.setUpDOM();
        } else {
          if (!this.isGroupedView()) {
            this.setUpPages();
          }
          this.updatePages();
        }
      }
      this.prevLength = len;
    });
  }
  private groupViewAccInitialized = false;
  private isGroupedViewInitialized = false;
  private prevIsGroupedView: boolean | undefined;
  private pageIndexInitialized = false;
  private prevPageIndex = 0;
  private lengthInitialized = false;
  private prevLength: number | undefined;
  pageSizeChange = output<DataObject>();
  pageEvent: PageEvent;
  private prevBtn: HTMLButtonElement;
  private firstBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private lastBtn: HTMLButtonElement;
  private pagesEl: HTMLDivElement;
  private numOfPages: number;
  private readonly maxPages = 6;
  private pageGroup = 1;
  /**
   * * An "unlisten" function for disposing of this handler.
   * * https://angular.io/api/core/Renderer2#listen
   */
  private listener: () => void;
  private readonly renderer = inject(Renderer2);
  ngAfterViewInit() {
    // * Create/Get necessary DOM elements...
    this.setUpDOM();
    this.renderer.addClass(this.pagesEl, 'pages-container');
    this.prevBtn?.insertAdjacentElement('afterend', this.pagesEl);
    if (this.length()) {
      if (!this.isGroupedView()) {
        this.setUpPages();
      }
      this.updatePages();
    }
  }
  ngOnDestroy() {
    this.listener?.();
  }
  onPaginatorChange(event: PageEvent): PageEvent {
    const { pageIndex, pageSize } = event;
    if (this.pageSize !== pageSize) {
      this.pageSize = pageSize;
      this.numOfPages = Math.ceil(this.length() / this.pageSize);
      this.pageGroup = Math.max(pageIndex % 2 === 0 ? pageIndex - 3 : pageIndex - 2, 1);
      // INFO: SetUp pages if the number of pages has changed...
      if (this.numOfPages !== this.maxPages) {
        this.cleanDOM(this.numOfPages);
        if (!this.isGroupedView()) {
          this.setUpPages();
        }
      }
    }
    if (pageIndex === 0) {
      // * Navigated to first page...
      this.pageGroup = 1;
    } else if (pageIndex === this.numOfPages - 1) {
      // * Navigated to last page...
      this.pageGroup = Math.max(1, this.numOfPages - this.maxPages + 1);
    } else if (this.maxPages + this.pageGroup - pageIndex === 2 && pageIndex < this.numOfPages - 1) {
      // * Navigated to any page from left to right...
      this.pageGroup += 2;
    } else if (pageIndex === this.pageGroup && this.pageGroup !== 1) {
      // * Navigated to any page from right to left...
      this.pageGroup -= 2;
    }
    this.pageIndex.set(pageIndex);
    this.updatePages(true);
      const page = {
        pageIndex: this.pageIndex() + 1,
        pageSize: this.pageSize,
      };
      this.pageSizeChange.emit(page);
    return event;
  }
  navigateToPage(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.nodeName === 'SPAN') {
      const pageValue = (event.target as HTMLSpanElement).getAttribute('data-page');
      const prevPage = this.pageIndex();
      if (pageValue === '+2' || pageValue === '-2') {
        this.pageGroup += Number(pageValue);
        this.pageIndex.set(this.pageGroup + 2);
      } else {
        this.pageIndex.set(Number(pageValue));
      }
      this.updatePages(true);
      if (this.useDataSource()) {
        // * Manually trigger page changes in case a table component is using datasource...
        const page: PageEvent = {
          previousPageIndex: prevPage,
          pageIndex: this.pageIndex(),
          pageSize: this.pageSize,
          length: this.length(),
        };
        this.dataPaginator().page.emit(page);
      }
    }
  }
  private setUpDOM(): void {
    this.firstBtn = this.paginator().nativeElement.querySelector('.mat-mdc-paginator-navigation-first');
    this.prevBtn = this.paginator().nativeElement.querySelector('.mat-mdc-paginator-navigation-previous');
    this.nextBtn = this.paginator().nativeElement.querySelector('.mat-mdc-paginator-navigation-next');
    this.lastBtn = this.paginator().nativeElement.querySelector('.mat-mdc-paginator-navigation-last');
    this.pagesEl = this.pagesEl ? this.pagesEl : this.renderer.createElement('DIV');
    // * Event delegation for pages...
    if (!this.listener) {
      this.listener = this.renderer.listen(this.pagesEl, 'click', this.navigateToPage.bind(this));
    }
  }
  private setUpPages(): void {
    this.numOfPages = Math.ceil(this.length() / this.pageSize);
    // * Create pages children...
    const pagesToShow = Math.min(this.numOfPages, this.maxPages);
    for (let page = 0; page < pagesToShow; page++) {
      const pageInstance = this.renderer.createElement('SPAN');
      this.renderer.appendChild(this.pagesEl, pageInstance);
    }
  }
  private updatePages(emitChange: boolean = false): void {
    if (!this.isGroupedView()) {
      Array.from(this.pagesEl.children).forEach((pageEl, index) => {
        const { label, value } = this.getPageData(index);
        this.renderer.setProperty(pageEl, 'innerText', label);
        this.renderer.setAttribute(pageEl, 'data-page', value);
        if (this.pageIndex().toString() === value) {
          this.renderer.addClass(pageEl, 'current-page');
        } else {
          this.renderer.removeClass(pageEl, 'current-page');
        }
      });
    }
    this.updateButtonStylesByGroupFlag(emitChange);
  }
  /**
   * If isGroupedView is true, only the firstPageButton and nextPageButton will be displayed
   * and if groupViewAcc is equal to the total, don't display next button.
   * Else, we call the default method.
   */
  private updateButtonStylesByGroupFlag(emitChange: boolean) {
    if (this.isGroupedView()) {
      this.renderer.removeStyle(this.firstBtn, 'display');
      this.renderer.removeStyle(this.nextBtn, 'display');
      this.renderer.setStyle(this.prevBtn, 'display', 'none');
      this.renderer.setStyle(this.lastBtn, 'display', 'none');
      if (this.groupViewAcc() === this.length()) {
        this.renderer.setStyle(this.nextBtn, 'display', 'none');
      }
      if (emitChange) {
        this.emitChanges();
      }
    } else {
      this.updateButtonStyles(emitChange);
    }
  }
  private updateButtonStyles(emitChange: boolean) {
    if (this.firstBtn) {
      if (this.pageIndex() === 0) {
        this.renderer.setStyle(this.firstBtn, 'display', 'none');
      } else {
        this.renderer.removeStyle(this.firstBtn, 'display');
      }
    }
    if (this.prevBtn) {
      if (this.pageIndex() === 0) {
        this.renderer.setStyle(this.prevBtn, 'display', 'none');
      } else {
        this.renderer.removeStyle(this.prevBtn, 'display');
      }
    }
    if (this.nextBtn) {
      if (this.pageIndex() === this.numOfPages - 1) {
        this.renderer.setStyle(this.nextBtn, 'display', 'none');
      } else {
        this.renderer.removeStyle(this.nextBtn, 'display');
      }
    }
    if (this.lastBtn) {
      if (this.pageIndex() === this.numOfPages - 1) {
        this.renderer.setStyle(this.lastBtn, 'display', 'none');
      } else {
        this.renderer.removeStyle(this.lastBtn, 'display');
      }
    }
    if (emitChange) {
      this.emitChanges();
    }
  }
  private getPageData(pageIndex: number): { label: string; value: string } {
    const pageLabel = pageIndex + this.pageGroup;
    const pageValue = pageIndex + this.pageGroup - 1;
    if (
      (pageIndex === this.maxPages - 1 && this.numOfPages > this.maxPages && pageLabel < this.numOfPages) ||
      (pageIndex === 0 && this.pageGroup !== 1)
    ) {
      return { label: '...', value: pageIndex > 0 ? '+2' : '-2' };
    }
    return { label: pageLabel.toString(), value: pageValue.toString() };
  }
  private emitChanges(): void {
    this.pageChange.emit(this.pageIndex() + 1);
  }
  private cleanDOM(numOfPages: number): void {
    // * Clean DOM Elements if length is less than max pages limit...
    this.pagesEl.innerHTML = '';
    if (this.pageIndex() > numOfPages - 1) {
      this.pageIndex.set(0);
      this.pageGroup = 1;
    }
  }
  private validateDOM(): boolean {
    return !!(this.pagesEl && this.firstBtn && this.nextBtn && this.prevBtn && this.lastBtn);
  }
}
